import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Panel } from 'primereact/panel';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { injectIntl } from 'react-intl';
import { useMutation } from '@apollo/react-hooks';
import { withAccount } from '../../../../components/AccountContext';
import ManageMandatory from './ManageMandatory';
import ManageDelete from './ManageDelete';
import EditQuestionDialog from './EditQuestionDialog';
import messages from '../intl-messages';
import { UPDATE_LOCAL_ARI_RANK } from '../mutations';
import { withGrowl } from '../../../../components/GrowlNotifications';
import EditAnswersDialog from './EditAnswersDialog';
import ManageAllowUseForSuperLinks from './ManageAllowUseForSuperLinks';
import ManageAllowUseForQuestionnaires from './ManageAllowUseForQuestionnaires';


const ManageLocalARIFields = ({
  currentAccount, data, intl: { formatMessage }, growl,
}) => {
  const [tableData, setTableData] = useState([]);
  const [localAriFields, setLocalAriFields] = useState([]);
  const [editQuestionDialogIsVisible, setEditQuestionDialogIsVisible] = useState(false);
  const [editQuestionDialogFieldData, setEditQuestionDialogFieldData] = useState(null);
  const [editAnswersDialogIsVisible, setEditAnswersDialogIsVisible] = useState(false);
  const [editAnswersDialogFieldData, setEditAnswersDialogFieldData] = useState();
  const [changingRank, setChangingRank] = useState(false);

  const [updateLocalAriRank] = useMutation(UPDATE_LOCAL_ARI_RANK, { refetchQueries: ['getAccount'] });

  useEffect(() => {
    if (data) {
      setLocalAriFields(
        _.sortBy(data.account.localAriFields, f => f.rank)
          .map(field => ({
            id: field.id,
            rank: field.rank,
            question: field.question,
            dataType: field.dataType,
            mandatory: field.mandatory,
            answers: field.answers,
          })),
      );

      setTableData(
        _.sortBy(data.account.localAriFields, f => f.rank)
          .map(field => ({
            id: field.id,
            rank: field.rank,
            question: field.question,
            type: field.dataType,
          })),
      );
    }
  }, [data]);

  const questionTemplate = (rowData) => {
    const field = _.find(localAriFields, local => local.id === rowData.id);
    return (
      <div>
        <div className="p-col-12">
          {rowData.question}
        </div>

        <div className="p-col-12">
          <Button
            icon="pi pi-angle-double-up"
            className="p-button-secondary"
            disabled={changingRank || rowData.rank === 1}
            onClick={() => {
              setChangingRank(true);

              const input = {
                accountId: currentAccount,
                ariFieldId: rowData.id,
                operation: 'DECREASE',
              };

              updateLocalAriRank({ variables: { input } })
                .then(() => {
                  growl.show({
                    severity: 'success',
                    sticky: false,
                    summary: formatMessage(messages.growlAddFieldSuccessSummary),
                    detail: formatMessage(messages.growlRankChangedSuccessDetail),
                  });
                  setChangingRank(false);
                })
                .catch(() => {
                  growl.show({
                    severity: 'error',
                    sticky: true,
                    summary: formatMessage(messages.growlAddFieldFailSummary),
                    detail: formatMessage(messages.growlRankChangedFailDetail),
                  });
                  setChangingRank(false);
                });
            }}
          />

          <Button
            className="p-button-secondary"
            label="..."
            onClick={() => {
              setEditQuestionDialogFieldData(field);
              setEditQuestionDialogIsVisible(true);
            }}
          />

          <Button
            icon="pi pi-angle-double-down"
            className="p-button-secondary"
            disabled={changingRank || rowData.rank === localAriFields.length}
            onClick={() => {
              setChangingRank(true);

              const input = {
                accountId: currentAccount,
                ariFieldId: rowData.id,
                operation: 'INCREASE',
              };

              updateLocalAriRank({ variables: { input } })
                .then(() => {
                  growl.show({
                    severity: 'success',
                    sticky: false,
                    summary: formatMessage(messages.growlAddFieldSuccessSummary),
                    detail: formatMessage(messages.growlRankChangedSuccessDetail),
                  });
                  setChangingRank(false);
                })
                .catch(() => {
                  growl.show({
                    severity: 'error',
                    sticky: true,
                    summary: formatMessage(messages.growlAddFieldFailSummary),
                    detail: formatMessage(messages.growlRankChangedFailDetail),
                  });
                  setChangingRank(false);
                });
            }}
          />
        </div>
      </div>
    );
  };

  const typeTemplate = (rowData) => {
    const field = _.find(localAriFields, local => local.id === rowData.id);
    return (
      <div>
        {rowData.type}
        {
          (field.dataType === 'DROPDOWN') && (
            <>
              <div style={{ fontStyle: 'italic' }}>
                {`${field.answers.length} ${formatMessage(messages.answersLabel)}`}
              </div>
              <Button
                label="..."
                className="p-button-secondary"
                onClick={() => {
                  setEditAnswersDialogFieldData(field);
                  setEditAnswersDialogIsVisible(true);
                }}
              />
            </>
          )
        }
      </div>
    );
  };

  const optionsTemplate = (rowData) => {
    const field = _.find(localAriFields, local => local.id === rowData.id);

    return (
      <div>
        <div className="p-col-12" style={{ margin: '0px', padding: '0px' }}>
          <ManageMandatory
            field={field}
            currentAccount={currentAccount}
          />
        </div>
      </div>
    );
  };

  const deleteTemplate = (rowData) => {
    const field = _.find(localAriFields, local => local.id === rowData.id);

    return (
      <div className="p-col-12" style={{ margin: '0px', padding: '0px' }}>
        <ManageDelete
          currentAccount={currentAccount}
          field={field}
        />
      </div>
    );
  };

  return (
    <div style={{ marginBottom: '10px', width: '100%' }}>
      <Panel
        className="p-col-12 sub-panel"
        header={formatMessage(messages.localManagePanelHeader)}
      >
        <div className="p-grid">
          <div className="p-col-12" style={{ textAlign: 'center' }}>
            <ManageAllowUseForSuperLinks
              data={data}
              currentAccount={currentAccount}
            />
          </div>
          <div className="p-col-12" style={{ textAlign: 'center' }}>
            <ManageAllowUseForQuestionnaires
              data={data}
              currentAccount={currentAccount}
            />
          </div>
          {localAriFields.length === 0
            ? (
              <div style={{ width: '100%', textAlign: 'center' }}>
                <small>
                  {formatMessage(messages.localManageNoFields)}
                </small>
              </div>
            )
            : (
              <>
                <DataTable value={tableData}>
                  <Column
                    field="question"
                    header={formatMessage(messages.questionLabel)}
                    body={questionTemplate}
                    style={{ textAlign: 'center', width: '10em', fontStyle: 'italic' }}
                  />
                  <Column
                    field="type"
                    header={formatMessage(messages.typeLabel)}
                    body={typeTemplate}
                    style={{ textAlign: 'center', width: '8em' }}
                  />
                  <Column
                    body={optionsTemplate}
                    style={{ textAlign: 'center', width: '8em' }}
                  />
                  <Column
                    body={deleteTemplate}
                    style={{ textAlign: 'center', width: '6em' }}
                  />
                </DataTable>

                {editQuestionDialogFieldData && (
                  <EditQuestionDialog
                    isVisible={editQuestionDialogIsVisible}
                    onHide={() => {
                      setEditQuestionDialogFieldData(null);
                      setEditAnswersDialogIsVisible(false);
                    }}
                    field={editQuestionDialogFieldData}
                    currentAccount={currentAccount}
                  />
                )}

                {editAnswersDialogFieldData && (
                  <EditAnswersDialog
                    isVisible={editAnswersDialogIsVisible}
                    onHide={() => {
                      setEditAnswersDialogFieldData(null);
                      setEditAnswersDialogIsVisible(false);
                    }}
                    field={editAnswersDialogFieldData}
                    currentAccount={currentAccount}
                  />
                )
                }
              </>
            )}
        </div>
      </Panel>
    </div>
  );
};

ManageLocalARIFields.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
  growl: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withAccount(injectIntl(withGrowl(ManageLocalARIFields)));
